<script lang="ts" setup>
// Onglets dossier v2 (port de chrome/SubTabs.jsx) — remplace OpportunityTabsComponent. Config des
// onglets + navigation partagées avec la tab bar mobile (useOpportunityTabs). Compteur : seul le
// score Analyse a une source réelle (useAnalyseScore), comme le legacy TabsComponent — on ne
// fabrique pas de compteurs simu/docs/messages.
import { onClickOutside } from '@vueuse/core';
import type { OpportunityTab } from '~/composables/useOpportunityTabs';
import { useCtrlHeld } from '~/composables/ds/useCtrlHeld';
import { isBrowserHandledClick } from '~/utils/popupNavigation';

const { tabs, isActive, tabHref, openTab } = useOpportunityTabs();
// Affordance de l'amont (chrome/SubTabs.jsx) ; le geste, lui, est natif.
const { ctrlHeld } = useCtrlHeld();
const { scorePercent, hasScore, severity } = useAnalyseScore();
const analyseTone = computed<'success' | 'orange' | 'gray'>(() =>
  severity.value === 'success' ? 'success' : severity.value === 'warn' ? 'orange' : 'gray');

// Onglets gardés en ligne, seuils de l'amont. Sous 1024px le chrome desktop est servi tant que la
// feature `mobile` est fermée : la ligne n'y tient que 2 onglets.
const { bp, isMobileBp } = useDsBreakpoint();
const inlineCount = computed<number>(() => {
  if (isMobileBp.value) { return 2; }
  if (bp.value === 'lg') { return 4; }
  if (bp.value === 'xl') { return 5; }
  return tabs.value.length;
});
const collapse = computed<boolean>(() => tabs.value.length > inlineCount.value);
const shownTabs = computed<OpportunityTab[]>(() =>
  (collapse.value ? tabs.value.slice(0, inlineCount.value) : tabs.value));
const restTabs = computed<OpportunityTab[]>(() =>
  (collapse.value ? tabs.value.slice(inlineCount.value) : []));
const restActive = computed<boolean>(() => restTabs.value.some(t => isActive(t)));

const moreOpen = ref(false);
const moreWrap = ref<HTMLElement | null>(null);
onClickOutside(moreWrap, () => { moreOpen.value = false; });
function onKey (e: KeyboardEvent): void { if (e.key === 'Escape') { moreOpen.value = false; } }
onMounted(() => { if (import.meta.client) { window.addEventListener('keydown', onKey); } });
onUnmounted(() => { if (import.meta.client) { window.removeEventListener('keydown', onKey); } });

// Repasser en grand écran laisse le menu ouvert sur un conteneur démonté sinon.
watch(collapse, (value) => { if (!value) { moreOpen.value = false; } });

// Clic simple : notre routage (SPA, ou popup pour Édition et Historique). Le reste au navigateur.
function onTabClick (tab: OpportunityTab, event: MouseEvent): void {
  if (isBrowserHandledClick(event)) { return; }
  event.preventDefault();
  openTab(tab);
}

// Le menu se referme dans tous les cas : sur un Ctrl+clic, le clic étant dans le menu,
// `onClickOutside` ne le fermerait pas.
function onMoreClick (tab: OpportunityTab, event: MouseEvent): void {
  moreOpen.value = false;
  onTabClick(tab, event);
}
</script>

<template>
  <div class="gd-subtabs">
    <a
      v-for="t in shownTabs"
      :key="t.key"
      :href="tabHref(t)"
      :class="['gd-subtab', { active: isActive(t) }]"
      @click="onTabClick(t, $event)"
    >
      <DsIcon
        :name="t.icon"
        :size="18"
      />
      <span>{{ t.label }}</span>
      <DsCount
        v-if="t.key === 'analysis' && hasScore"
        :tone="analyseTone"
      >
        {{ scorePercent }}
      </DsCount>
      <span
        v-if="ctrlHeld && tabHref(t)"
        class="gd-subtab-newtab"
        aria-hidden="true"
        :title="`Ouvrir « ${t.label} » dans un nouvel onglet`"
      >
        <DsIcon
          name="external-link"
          :size="12"
        />
      </span>
    </a>

    <span
      v-if="collapse"
      ref="moreWrap"
      class="gd-subtab-more-wrap"
    >
      <button
        type="button"
        :class="['gd-subtab', 'gd-subtab-more', { active: restActive }]"
        title="Autres onglets"
        aria-label="Autres onglets"
        aria-haspopup="menu"
        :aria-expanded="moreOpen"
        @click="moreOpen = !moreOpen"
      >
        <DsIcon
          name="angle-down"
          :size="16"
        />
      </button>
      <div
        v-if="moreOpen"
        class="gd-subtab-more-menu"
        role="menu"
      >
        <a
          v-for="t in restTabs"
          :key="t.key"
          :href="tabHref(t)"
          role="menuitem"
          :class="['gd-subtab-more-item', { active: isActive(t) }]"
          @click="onMoreClick(t, $event)"
        >
          <DsIcon
            :name="t.icon"
            :size="16"
          />
          <span>{{ t.label }}</span>
          <DsCount
            v-if="t.key === 'analysis' && hasScore"
            :tone="analyseTone"
          >
            {{ scorePercent }}
          </DsCount>
          <span
            v-if="ctrlHeld && tabHref(t)"
            class="gd-subtab-newtab"
            aria-hidden="true"
            :title="`Ouvrir « ${t.label} » dans un nouvel onglet`"
          >
            <DsIcon
              name="external-link"
              :size="12"
            />
          </span>
        </a>
      </div>
    </span>
  </div>
</template>
